import { NavigatorImagesData } from 'business/models/CustomModels';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import InspectionService from 'business/services/InspectionService';
import {
  getWellNumbersWithinRangeHorizontal,
  getWellNumbersWithinRangeVertically,
} from 'components/plateviewer/well/WellImage/utils';
import { wellData } from 'components/plateviewer/well/WellImage/WellImage';
import { useCallback } from 'react';
import { useRMSelector } from 'redux/hooks';
import { getNavigatorImageKeysAsString } from './utils';
import {
  getNavigatorImageStoreByKey,
  setNavigatorImageStoreByKey,
} from 'redux/reducers/experimentDataReducer';
import { EFetchingStatus } from 'enums/EFetchingStatus';
import { appendNavigatorImagesAvailableKeys } from 'redux/reducers/experimentReducer';
import {
  DropImageParameters,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { useUpdateCompositeImagesHandler } from 'components/Experiment/hooks';
import { DisplayModes, GetDisplayMode } from 'utils/Global';

export const usePlateDrawaingIdentifiers = () => {
  return (plateId: number) => {
    const plate = useRMSelector((state) =>
      state?.experiment?.expData?.plateDto.find((x) => x.plateId == plateId),
    );
    if (!plate) {
      return;
    }
    return plate.drawingIdentifiers;
  };
};

export const usePlateDisableImageRegions = () => {
  return (plateId: number) => {
    const plate = useRMSelector((state) =>
      state?.experiment?.expData?.plateDto.find((x) => x.plateId == plateId),
    );
    if (!plate) {
      return;
    }
    return plate.drawingIdentifiers?.plateDropRegions;
  };
};

export const useFetchNavigatorImages = (
  navigatorVerticalPrefetchingCount: number,
  navigatorHorizontalPrefetchingCount: number,
) => {
  const fetchNavigatorImages = useCallback(
    (
      exp: ExperimentDtoClient,
      wellNavigationDetails: WellNavigationDetails,
      DropImageParameters: DropImageParameters,
      dispatch: any,
    ) => {
      const inspService = new InspectionService();
      const plateDetails = exp.plateDto[0];
      const { cpId, dropNumber, inspection, regionName } = DropImageParameters || {};
      const imageBatchId = inspection?.groupedInspections?.find(
        (grp) => grp.plateId === wellNavigationDetails?.plateID,
      )?.imageBatchId;

      const fetchData = async () => {
        const wellNumbersVertical = getWellNumbersWithinRangeVertically(
          wellNavigationDetails?.wellNum,
          navigatorVerticalPrefetchingCount >= 0
            ? navigatorVerticalPrefetchingCount * 2
            : 10,
          plateDetails.cols,
          plateDetails.rows,
          [],
          wellNavigationDetails?.plateID,
          exp,
        );
        const wellNumbersHorizontal = getWellNumbersWithinRangeHorizontal(
          wellNavigationDetails?.wellNum,
          navigatorHorizontalPrefetchingCount >= 0
            ? navigatorHorizontalPrefetchingCount * 2
            : 10,
          1,
          plateDetails.rows * plateDetails.cols,
          [],
          wellNavigationDetails?.plateID,
          exp,
        );
        const wells: wellData[] = [
          ...wellNumbersVertical,
          ...wellNumbersHorizontal,
        ].reduce((accumulator, current) => {
          const existingWell = accumulator.find(
            (well) =>
              well.wellNum === current.wellNum &&
              well.plateNum === current.plateNum &&
              well.plateID === current.plateID,
          );
          if (!existingWell) {
            accumulator.push(current);
          }
          return accumulator;
        }, []);
        const processImage = async (well: wellData) => {
          const { plateID, wellNum } = well;
          const compositeKey = getNavigatorImageKeysAsString(well, DropImageParameters);
          const canRequestNavigatorImages =
            !!imageBatchId &&
            cpId != null &&
            dropNumber != null &&
            regionName != null &&
            plateID != null &&
            wellNum != null;
          if (!getNavigatorImageStoreByKey(compositeKey) && canRequestNavigatorImages) {
            setNavigatorImageStoreByKey(compositeKey, {
              status: EFetchingStatus.InProgress,
              data: null,
            });
            const result = await inspService.GetNavigatorImages(
              exp?.experimentId,
              plateID,
              wellNum,
              imageBatchId,
              cpId,
              dropNumber,
              regionName,
            );
            dispatch(appendNavigatorImagesAvailableKeys(compositeKey));
            setNavigatorImageStoreByKey(compositeKey, {
              status: EFetchingStatus.Loaded,
              data: result,
            });
          }
        };

        // filter out invalid plates (plateID <= 0) before creating promises
        const validWells = wells.filter((w) => w && w.plateID > 0);
        const currentWell = validWells.find(
          (w) =>
            w.wellNum === wellNavigationDetails.wellNum &&
            w.plateID === wellNavigationDetails.plateID,
        );
        if (currentWell) {
          await processImage(currentWell);
        }
        const prefetchWells = validWells.filter((w) => w !== currentWell);
        if (prefetchWells.length) {
          Promise.all(prefetchWells.map(processImage));
        }
      };

      if (exp?.experimentId) {
        fetchData();
      }
    },
    [],
  );

  return fetchNavigatorImages;
};

export const useUpdateCompositeImage = () => {
  const updateCompositeImage = useUpdateCompositeImagesHandler();
  const displayMode = GetDisplayMode();
  return (wellNavigationDetails, dropImageParameters) => {
    if (displayMode == DisplayModes.IMAGE) {
      updateCompositeImage(wellNavigationDetails, dropImageParameters);
    }
  };
};

export const useNavigatorInspections = (compositeKey: string) => {
  const imageData = getNavigatorImageStoreByKey(compositeKey) as NavigatorImagesData;
  if (imageData && imageData.status == EFetchingStatus.Loaded) {
    return imageData.data?.navigatorInspections;
  }
};

export const useNavigatorRegions = (compositeKey: string) => {
  const imageData = getNavigatorImageStoreByKey(compositeKey) as NavigatorImagesData;
  if (imageData && imageData.status == EFetchingStatus.Loaded) {
    return imageData.data?.navigatorRegions;
  }
};

export const useNavigatorImagingSettings = (compositeKey: string) => {
  const imageData = getNavigatorImageStoreByKey(compositeKey) as NavigatorImagesData;
  if (imageData && imageData.status == EFetchingStatus.Loaded) {
    return imageData.data?.navigatorImagingSettings;
  }
};

export const useNavigatorDrops = (compositeKey: string) => {
  const imageData = getNavigatorImageStoreByKey(compositeKey) as NavigatorImagesData;
  if (imageData && imageData.status == EFetchingStatus.Loaded) {
    return imageData.data?.navigatorDrop;
  }
};
